import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Plus, Shield, Pencil, Trash2, Eye, EyeOff } from 'lucide-react';
import { clientPasswordsAPI } from '../../services/api';

const ClientPasswordsTab = ({ 
  clientId, 
  clientPasswords, 
  onRefresh,
  toast 
}) => {
  const [passwordDialog, setPasswordDialog] = useState(false);
  const [editPasswordDialog, setEditPasswordDialog] = useState(false);
  const [editingPassword, setEditingPassword] = useState(null);
  const [newPassword, setNewPassword] = useState({ serviceName: '', username: '', password: '', website: '' });
  const [revealedPasswords, setRevealedPasswords] = useState({});

  const handleCreatePassword = async () => {
    if (!newPassword.serviceName || !newPassword.password) {
      toast({ title: 'Error', description: 'Please fill in required fields', variant: 'destructive' });
      return;
    }
    try {
      await clientPasswordsAPI.create(clientId, newPassword);
      toast({ title: 'Success', description: 'Password saved securely' });
      setPasswordDialog(false);
      setNewPassword({ serviceName: '', username: '', password: '', website: '' });
      onRefresh();
    } catch (error) {
      console.error('Error saving password:', error);
      toast({ title: 'Error', description: 'Failed to save password', variant: 'destructive' });
    }
  };

  const handleUpdatePassword = async () => {
    if (!editingPassword) return;
    try {
      await clientPasswordsAPI.update(clientId, editingPassword._id, {
        serviceName: editingPassword.serviceName,
        username: editingPassword.username,
        password: editingPassword.newPassword || undefined,
        website: editingPassword.website
      });
      toast({ title: 'Success', description: 'Password updated successfully' });
      setEditPasswordDialog(false);
      setEditingPassword(null);
      onRefresh();
    } catch (error) {
      console.error('Error updating password:', error);
      toast({ title: 'Error', description: 'Failed to update password', variant: 'destructive' });
    }
  };

  const handleDeletePassword = async (passwordId) => {
    if (!window.confirm('Are you sure you want to delete this password?')) return;
    try {
      await clientPasswordsAPI.delete(clientId, passwordId);
      toast({ title: 'Success', description: 'Password deleted' });
      setRevealedPasswords(prev => {
        const updated = { ...prev };
        delete updated[passwordId];
        return updated;
      });
      onRefresh();
    } catch (error) {
      console.error('Error deleting password:', error);
      toast({ title: 'Error', description: 'Failed to delete password', variant: 'destructive' });
    }
  };

  const handleRevealPassword = async (passwordId) => {
    if (revealedPasswords[passwordId]) {
      setRevealedPasswords(prev => {
        const updated = { ...prev };
        delete updated[passwordId];
        return updated;
      });
    } else {
      try {
        const response = await clientPasswordsAPI.reveal(clientId, passwordId);
        setRevealedPasswords(prev => ({
          ...prev,
          [passwordId]: response.data.password
        }));
      } catch (error) {
        console.error('Error revealing password:', error);
        toast({ title: 'Error', description: 'Failed to reveal password', variant: 'destructive' });
      }
    }
  };

  const openEditPasswordDialog = (pwd) => {
    setEditingPassword({ ...pwd, newPassword: '' });
    setEditPasswordDialog(true);
  };

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Stored Passwords
            </CardTitle>
            <Button size="sm" className="gap-2" onClick={() => setPasswordDialog(true)} data-testid="add-password-btn">
              <Plus className="h-4 w-4" />
              Add Password
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {clientPasswords.length > 0 ? (
            <div className="space-y-3">
              {clientPasswords.map((pwd) => (
                <div key={pwd._id} className="p-4 border rounded-lg hover:border-blue-300 transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900">{pwd.serviceName}</h4>
                      {pwd.website && (
                        <a href={pwd.website} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline">
                          {pwd.website}
                        </a>
                      )}
                      <div className="mt-2 space-y-1">
                        {pwd.username && (
                          <p className="text-sm text-gray-600">
                            <span className="text-gray-500">Username:</span> {pwd.username}
                          </p>
                        )}
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-500">Password:</span>
                          {revealedPasswords[pwd._id] ? (
                            <span className="text-sm font-mono bg-gray-100 px-2 py-0.5 rounded">{revealedPasswords[pwd._id]}</span>
                          ) : (
                            <span className="text-sm">••••••••</span>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 text-xs gap-1"
                            onClick={() => handleRevealPassword(pwd._id)}
                          >
                            {revealedPasswords[pwd._id] ? (
                              <>
                                <EyeOff className="h-3 w-3" />
                                Hide
                              </>
                            ) : (
                              <>
                                <Eye className="h-3 w-3" />
                                Show
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => openEditPasswordDialog(pwd)}
                      >
                        <Pencil className="h-3 w-3" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-red-500 hover:text-red-700"
                        onClick={() => handleDeletePassword(pwd._id)}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-gray-500 py-8">No passwords stored yet</p>
          )}
        </CardContent>
      </Card>

      {/* Add Password Dialog */}
      <Dialog open={passwordDialog} onOpenChange={setPasswordDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Password</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Service Name *</Label>
              <Input 
                placeholder="e.g., CRA My Account"
                value={newPassword.serviceName} 
                onChange={(e) => setNewPassword(prev => ({ ...prev, serviceName: e.target.value }))} 
              />
            </div>
            <div className="space-y-2">
              <Label>Website URL</Label>
              <Input 
                placeholder="https://..."
                value={newPassword.website} 
                onChange={(e) => setNewPassword(prev => ({ ...prev, website: e.target.value }))} 
              />
            </div>
            <div className="space-y-2">
              <Label>Username</Label>
              <Input 
                placeholder="Username or email"
                value={newPassword.username} 
                onChange={(e) => setNewPassword(prev => ({ ...prev, username: e.target.value }))} 
              />
            </div>
            <div className="space-y-2">
              <Label>Password *</Label>
              <Input 
                type="password"
                placeholder="Password"
                value={newPassword.password} 
                onChange={(e) => setNewPassword(prev => ({ ...prev, password: e.target.value }))} 
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPasswordDialog(false)}>Cancel</Button>
            <Button onClick={handleCreatePassword}>Save Password</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Password Dialog */}
      <Dialog open={editPasswordDialog} onOpenChange={setEditPasswordDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Password</DialogTitle>
          </DialogHeader>
          {editingPassword && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Service Name *</Label>
                <Input 
                  value={editingPassword.serviceName} 
                  onChange={(e) => setEditingPassword(prev => ({ ...prev, serviceName: e.target.value }))} 
                />
              </div>
              <div className="space-y-2">
                <Label>Website URL</Label>
                <Input 
                  value={editingPassword.website || ''} 
                  onChange={(e) => setEditingPassword(prev => ({ ...prev, website: e.target.value }))} 
                />
              </div>
              <div className="space-y-2">
                <Label>Username</Label>
                <Input 
                  value={editingPassword.username || ''} 
                  onChange={(e) => setEditingPassword(prev => ({ ...prev, username: e.target.value }))} 
                />
              </div>
              <div className="space-y-2">
                <Label>New Password (leave blank to keep current)</Label>
                <Input 
                  type="password"
                  placeholder="New password"
                  value={editingPassword.newPassword || ''} 
                  onChange={(e) => setEditingPassword(prev => ({ ...prev, newPassword: e.target.value }))} 
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditPasswordDialog(false)}>Cancel</Button>
            <Button onClick={handleUpdatePassword}>Update</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ClientPasswordsTab;
